import { defaultCardsColor, normalizeColor } from "./cardTools";

export const emailSignatureFieldOptions = [
  ["role", "Ruolo"],
  ["companyName", "Azienda"],
  ["mobile", "Cellulare"],
  ["phone", "Telefono ufficio"],
  ["email", "Email"],
  ["website", "Sito web"],
  ["address", "Indirizzo"],
  ["cardUrl", "Link Cards"],
  ["socialLinks", "Loghi social"],
  ["qrCode", "QR code"],
];

export const defaultEmailSignatureFields = emailSignatureFieldOptions
  .map(([id]) => id)
  .filter((id) => id !== "qrCode");

const escapeHtml = (value = "") =>
  String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

const escapeAttr = escapeHtml;

const isAbsoluteHttpUrl = (value = "") => /^https?:\/\//i.test(value);

const shouldShow = (visibleFields, fieldName) =>
  visibleFields.includes(fieldName);

const filterData = (data, visibleFields = defaultEmailSignatureFields) => ({
  ...data,
  role: shouldShow(visibleFields, "role") ? data.role : "",
  companyName: shouldShow(visibleFields, "companyName")
    ? data.companyName
    : "",
  mobile: shouldShow(visibleFields, "mobile") ? data.mobile : "",
  phone: shouldShow(visibleFields, "phone") ? data.phone : "",
  email: shouldShow(visibleFields, "email") ? data.email : "",
  website: shouldShow(visibleFields, "website") ? data.website : "",
  address: shouldShow(visibleFields, "address") ? data.address : "",
  cardUrl: shouldShow(visibleFields, "cardUrl") ? data.cardUrl : "",
  showSocialLinks: shouldShow(visibleFields, "socialLinks"),
  qrCodeDataUrl: shouldShow(visibleFields, "qrCode")
    ? data.qrCodeDataUrl
    : "",
});

const line = (label, value, href = "") => {
  if (!value) return "";
  const escapedValue = escapeHtml(value);

  if (href) {
    return `<tr><td style="padding:1px 0;font-family:Arial,sans-serif;font-size:12px;line-height:18px;color:#334155;"><strong style="color:#0f172a;">${escapeHtml(label)}:</strong> <a href="${escapeAttr(href)}" style="color:#334155;text-decoration:none;">${escapedValue}</a></td></tr>`;
  }

  return `<tr><td style="padding:1px 0;font-family:Arial,sans-serif;font-size:12px;line-height:18px;color:#334155;"><strong style="color:#0f172a;">${escapeHtml(label)}:</strong> ${escapedValue}</td></tr>`;
};

const getContactRows = (data) =>
  [
    line("Mobile", data.mobile, data.mobile ? `tel:${data.mobile}` : ""),
    line("Tel", data.phone, data.phone ? `tel:${data.phone}` : ""),
    line("Email", data.email, data.email ? `mailto:${data.email}` : ""),
    line("Web", data.website, data.website),
    line("Indirizzo", data.address),
  ].join("");

const getPlainContactRows = (data) =>
  [
    data.mobile ? `Mobile: ${data.mobile}` : "",
    data.phone ? `Tel: ${data.phone}` : "",
    data.email ? `Email: ${data.email}` : "",
    data.website ? `Web: ${data.website}` : "",
    data.address ? `Indirizzo: ${data.address}` : "",
    data.cardUrl ? `Cards: ${data.cardUrl}` : "",
    data.linkedin ? `LinkedIn: ${data.linkedin}` : "",
    data.facebook ? `Facebook: ${data.facebook}` : "",
    data.instagram ? `Instagram: ${data.instagram}` : "",
    data.twitter ? `X/Twitter: ${data.twitter}` : "",
  ].filter(Boolean);

const logoBlock = (url, alt, maxWidth = 120) => {
  if (!isAbsoluteHttpUrl(url)) return "";

  return `<img src="${escapeAttr(url)}" width="${maxWidth}" alt="${escapeAttr(alt)}" style="display:block;width:${maxWidth}px;max-width:${maxWidth}px;height:auto;border:0;outline:none;text-decoration:none;">`;
};

const avatarBlock = (url, alt, size = 72) => {
  if (!isAbsoluteHttpUrl(url)) return "";

  return `<img src="${escapeAttr(url)}" width="${size}" height="${size}" alt="${escapeAttr(alt)}" style="display:block;width:${size}px;height:${size}px;object-fit:cover;border-radius:${Math.round(size / 2)}px;border:0;outline:none;text-decoration:none;">`;
};

const socialDefinitions = [
  ["linkedin", "in", "LinkedIn"],
  ["facebook", "f", "Facebook"],
  ["instagram", "ig", "Instagram"],
  ["twitter", "x", "X"],
  ["youtube", "yt", "YouTube"],
  ["tiktok", "tt", "TikTok"],
];

const socialLinksBlock = (data, color) => {
  if (!data.showSocialLinks) return "";

  const links = socialDefinitions
    .filter(([field]) => isAbsoluteHttpUrl(data[field]))
    .map(
      ([field, label, title]) =>
        `<td style="padding:8px 5px 0 0;"><a href="${escapeAttr(data[field])}" title="${escapeAttr(title)}" style="display:inline-block;width:22px;height:22px;border-radius:11px;background:${color};font-family:Arial,sans-serif;font-size:10px;line-height:22px;text-align:center;color:#ffffff;text-decoration:none;font-weight:700;">${escapeHtml(label)}</a></td>`,
    )
    .join("");

  return links
    ? `<tr><td style="padding:0;"><table role="presentation" cellpadding="0" cellspacing="0" border="0" style="border-collapse:collapse;"><tr>${links}</tr></table></td></tr>`
    : "";
};

const qrBlock = (data, label = "Scansiona la Cards") => {
  if (!data.qrCodeDataUrl || !data.cardUrl) return "";

  return `
    <tr>
      <td style="padding-top:10px;">
        <table role="presentation" cellpadding="0" cellspacing="0" border="0" style="border-collapse:collapse;">
          <tr>
            <td style="padding:0 8px 0 0;">
              <a href="${escapeAttr(data.cardUrl)}" style="text-decoration:none;">
                <img src="${escapeAttr(data.qrCodeDataUrl)}" width="74" height="74" alt="QR code Cards" style="display:block;width:74px;height:74px;border:0;outline:none;text-decoration:none;">
              </a>
            </td>
            <td valign="middle" style="font-family:Arial,sans-serif;font-size:11px;line-height:15px;color:#64748b;">
              ${escapeHtml(label)}
            </td>
          </tr>
        </table>
      </td>
    </tr>`;
};

const baseNameBlock = (data, color) => `
  <tr>
    <td style="padding:0 0 3px;font-family:Arial,sans-serif;font-size:16px;line-height:20px;font-weight:700;color:#0f172a;">${escapeHtml(data.displayName)}</td>
  </tr>
  ${data.role ? `<tr><td style="padding:0 0 2px;font-family:Arial,sans-serif;font-size:12px;line-height:17px;color:${color};font-weight:700;">${escapeHtml(data.role)}</td></tr>` : ""}
  ${data.companyName ? `<tr><td style="padding:0 0 8px;font-family:Arial,sans-serif;font-size:12px;line-height:17px;color:#475569;">${escapeHtml(data.companyName)}</td></tr>` : ""}
`;

const footerLink = (data, color) =>
  data.cardUrl
    ? `<tr><td style="padding-top:8px;font-family:Arial,sans-serif;font-size:12px;line-height:18px;"><a href="${escapeAttr(data.cardUrl)}" style="color:${color};font-weight:700;text-decoration:none;">Apri la mia Cards</a></td></tr>`
    : "";

const templates = {
  minimal: {
    label: "Minimal",
    renderHtml: (data, color) => `
      <table role="presentation" cellpadding="0" cellspacing="0" border="0" style="border-collapse:collapse;">
        <tr>
          <td style="border-left:3px solid ${color};padding-left:12px;">
            <table role="presentation" cellpadding="0" cellspacing="0" border="0" style="border-collapse:collapse;">
              ${baseNameBlock(data, color)}
              ${getContactRows(data)}
              ${footerLink(data, color)}
              ${socialLinksBlock(data, color)}
              ${qrBlock(data)}
            </table>
          </td>
        </tr>
      </table>`,
  },
  corporate: {
    label: "Corporate con logo",
    renderHtml: (data, color) => `
      <table role="presentation" cellpadding="0" cellspacing="0" border="0" style="border-collapse:collapse;">
        <tr>
          ${
            isAbsoluteHttpUrl(data.logoUrl)
              ? `<td valign="top" style="padding:0 16px 0 0;">${logoBlock(data.logoUrl, data.companyName || data.displayName, 118)}</td>`
              : ""
          }
          <td valign="top" style="padding:0;">
            <table role="presentation" cellpadding="0" cellspacing="0" border="0" style="border-collapse:collapse;">
              ${baseNameBlock(data, color)}
              <tr><td style="padding:0 0 8px;border-top:2px solid ${color};font-size:1px;line-height:1px;">&nbsp;</td></tr>
              ${getContactRows(data)}
              ${footerLink(data, color)}
              ${socialLinksBlock(data, color)}
              ${qrBlock(data)}
            </table>
          </td>
        </tr>
      </table>`,
  },
  photo: {
    label: "Con foto profilo",
    renderHtml: (data, color) => `
      <table role="presentation" cellpadding="0" cellspacing="0" border="0" style="border-collapse:collapse;">
        <tr>
          ${
            isAbsoluteHttpUrl(data.avatarUrl)
              ? `<td valign="top" style="padding:0 14px 0 0;">${avatarBlock(data.avatarUrl, data.displayName)}</td>`
              : ""
          }
          <td valign="top" style="padding:0;">
            <table role="presentation" cellpadding="0" cellspacing="0" border="0" style="border-collapse:collapse;">
              ${baseNameBlock(data, color)}
              ${getContactRows(data)}
              ${footerLink(data, color)}
              ${socialLinksBlock(data, color)}
              ${qrBlock(data)}
            </table>
          </td>
        </tr>
      </table>`,
  },
  brandPhoto: {
    label: "Logo + foto",
    renderHtml: (data, color) => `
      <table role="presentation" cellpadding="0" cellspacing="0" border="0" style="border-collapse:collapse;">
        <tr>
          <td valign="top" style="padding:0 16px 0 0;">
            ${avatarBlock(data.avatarUrl, data.displayName, 70)}
            ${isAbsoluteHttpUrl(data.logoUrl) ? `<div style="height:10px;line-height:10px;font-size:10px;">&nbsp;</div>${logoBlock(data.logoUrl, data.companyName || data.displayName, 96)}` : ""}
          </td>
          <td valign="top" style="padding:0 0 0 16px;border-left:2px solid ${color};">
            <table role="presentation" cellpadding="0" cellspacing="0" border="0" style="border-collapse:collapse;">
              ${baseNameBlock(data, color)}
              ${getContactRows(data)}
              ${footerLink(data, color)}
              ${socialLinksBlock(data, color)}
              ${qrBlock(data)}
            </table>
          </td>
        </tr>
      </table>`,
  },
  cta: {
    label: "Commerciale con CTA",
    renderHtml: (data, color) => `
      <table role="presentation" cellpadding="0" cellspacing="0" border="0" style="border-collapse:collapse;min-width:300px;">
        <tr>
          <td style="padding:14px 16px;border:1px solid #dbe4ea;border-radius:8px;">
            <table role="presentation" cellpadding="0" cellspacing="0" border="0" style="border-collapse:collapse;">
              ${baseNameBlock(data, color)}
              ${getContactRows(data)}
              ${
                data.cardUrl
                  ? `<tr><td style="padding-top:12px;"><a href="${escapeAttr(data.cardUrl)}" style="display:inline-block;background:${color};color:#ffffff;font-family:Arial,sans-serif;font-size:12px;line-height:16px;font-weight:700;text-decoration:none;padding:9px 12px;border-radius:4px;">Salva i miei contatti</a></td></tr>`
                  : ""
              }
              ${socialLinksBlock(data, color)}
              ${qrBlock(data)}
            </table>
          </td>
        </tr>
      </table>`,
  },
  compact: {
    label: "Compatta",
    renderHtml: (data, color) => `
      <table role="presentation" cellpadding="0" cellspacing="0" border="0" style="border-collapse:collapse;">
        <tr>
          <td style="padding:0;font-family:Arial,sans-serif;font-size:12px;line-height:18px;color:#334155;">
            <strong style="font-size:14px;color:#0f172a;">${escapeHtml(data.displayName)}</strong>
            ${data.role ? ` · <span style="color:${color};font-weight:700;">${escapeHtml(data.role)}</span>` : ""}
            ${data.companyName ? ` · ${escapeHtml(data.companyName)}` : ""}
            <br>
            ${[
              data.mobile ? `M ${escapeHtml(data.mobile)}` : "",
              data.phone ? `T ${escapeHtml(data.phone)}` : "",
              data.email ? `<a href="mailto:${escapeAttr(data.email)}" style="color:#334155;text-decoration:none;">${escapeHtml(data.email)}</a>` : "",
              data.website ? `<a href="${escapeAttr(data.website)}" style="color:#334155;text-decoration:none;">${escapeHtml(data.website)}</a>` : "",
            ].filter(Boolean).join(" | ")}
          </td>
        </tr>
        ${data.cardUrl ? `<tr><td style="padding-top:5px;font-family:Arial,sans-serif;font-size:12px;line-height:18px;"><a href="${escapeAttr(data.cardUrl)}" style="color:${color};font-weight:700;text-decoration:none;">Cards digitale</a></td></tr>` : ""}
        ${qrBlock(data, "QR Cards")}
      </table>`,
  },
  digitalCard: {
    label: "Biglietto digitale",
    renderHtml: (data, color) => `
      <table role="presentation" cellpadding="0" cellspacing="0" border="0" style="border-collapse:collapse;">
        <tr>
          <td style="padding:0;background:#ffffff;border:1px solid #dbe4ea;border-radius:8px;">
            <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="430" style="width:430px;border-collapse:collapse;">
              <tr>
                <td colspan="3" style="height:6px;background:${color};font-size:6px;line-height:6px;">&nbsp;</td>
              </tr>
              <tr>
                <td valign="top" width="86" style="width:86px;padding:16px 0 16px 16px;">
                  ${
                    isAbsoluteHttpUrl(data.avatarUrl)
                      ? avatarBlock(data.avatarUrl, data.displayName, 70)
                      : `<div style="width:70px;height:70px;border-radius:35px;background:${color};font-family:Arial,sans-serif;font-size:24px;line-height:70px;text-align:center;color:#ffffff;font-weight:700;">${escapeHtml(data.displayName.slice(0, 1).toUpperCase())}</div>`
                  }
                </td>
                <td valign="top" style="padding:16px 16px 16px 12px;">
                  <table role="presentation" cellpadding="0" cellspacing="0" border="0" style="border-collapse:collapse;">
                    <tr>
                      <td style="padding:0 0 3px;font-family:Arial,sans-serif;font-size:17px;line-height:21px;font-weight:700;color:#0f172a;">${escapeHtml(data.displayName)}</td>
                    </tr>
                    ${data.role ? `<tr><td style="padding:0 0 2px;font-family:Arial,sans-serif;font-size:12px;line-height:17px;color:${color};font-weight:700;">${escapeHtml(data.role)}</td></tr>` : ""}
                    ${data.companyName ? `<tr><td style="padding:0 0 10px;font-family:Arial,sans-serif;font-size:12px;line-height:17px;color:#475569;">${escapeHtml(data.companyName)}</td></tr>` : ""}
                    ${getContactRows(data)}
                    ${footerLink(data, color)}
                    ${socialLinksBlock(data, color)}
                  </table>
                </td>
                ${
                  data.qrCodeDataUrl && data.cardUrl
                    ? `<td valign="top" width="94" style="width:94px;padding:16px 16px 16px 0;text-align:center;">
                        <a href="${escapeAttr(data.cardUrl)}" style="text-decoration:none;">
                          <img src="${escapeAttr(data.qrCodeDataUrl)}" width="78" height="78" alt="QR code Cards" style="display:block;width:78px;height:78px;border:1px solid #e2e8f0;outline:none;text-decoration:none;">
                        </a>
                        <div style="padding-top:5px;font-family:Arial,sans-serif;font-size:10px;line-height:13px;color:#64748b;text-align:center;">Apri Cards</div>
                      </td>`
                    : ""
                }
              </tr>
            </table>
          </td>
        </tr>
      </table>`,
  },
  qrSpotlight: {
    label: "QR in evidenza",
    renderHtml: (data, color) => `
      <table role="presentation" cellpadding="0" cellspacing="0" border="0" style="border-collapse:collapse;">
        <tr>
          <td valign="top" style="padding:0 18px 0 0;">
            ${
              data.qrCodeDataUrl && data.cardUrl
                ? `<a href="${escapeAttr(data.cardUrl)}" style="text-decoration:none;"><img src="${escapeAttr(data.qrCodeDataUrl)}" width="104" height="104" alt="QR code Cards" style="display:block;width:104px;height:104px;border:3px solid ${color};outline:none;text-decoration:none;"></a>`
                : logoBlock(data.logoUrl, data.companyName || data.displayName, 104)
            }
          </td>
          <td valign="top" style="padding:0;">
            <table role="presentation" cellpadding="0" cellspacing="0" border="0" style="border-collapse:collapse;">
              ${baseNameBlock(data, color)}
              ${getContactRows(data)}
              ${data.cardUrl ? `<tr><td style="padding-top:9px;font-family:Arial,sans-serif;font-size:12px;line-height:18px;"><a href="${escapeAttr(data.cardUrl)}" style="display:inline-block;background:${color};color:#ffffff;font-weight:700;text-decoration:none;padding:7px 10px;border-radius:4px;">Apri la Cards</a></td></tr>` : ""}
              ${socialLinksBlock(data, color)}
            </table>
          </td>
        </tr>
      </table>`,
  },
};

export const emailSignatureTemplates = Object.entries(templates).map(
  ([id, template]) => ({
    id,
    label: template.label,
  }),
);

export const buildEmailSignatureHtml = ({
  data,
  templateId = "minimal",
  primaryColor = defaultCardsColor,
  visibleFields = defaultEmailSignatureFields,
}) => {
  const color = normalizeColor(primaryColor);
  const template = templates[templateId] || templates.minimal;
  const filteredData = filterData(data, visibleFields);

  return template.renderHtml(filteredData, color).replace(/\n\s+/g, "");
};

export const buildEmailSignatureText = (
  data,
  visibleFields = defaultEmailSignatureFields,
) => {
  const filteredData = filterData(data, visibleFields);

  return [
    filteredData.displayName,
    filteredData.role,
    filteredData.companyName,
    ...getPlainContactRows(
      filteredData.showSocialLinks
        ? filteredData
        : {
            ...filteredData,
            linkedin: "",
            facebook: "",
            instagram: "",
            twitter: "",
            youtube: "",
            tiktok: "",
          },
    ),
  ]
    .filter(Boolean)
    .join("\n");
};
